import VehicleTransfer from "../components/VehicleTransfer";
import VehicleLifecycle, { VehicleLifecycleNotice } from "../components/VehicleLifecycle";
import {can} from "../lib/permissions";
import RentalProgress from '../components/RentalProgress';
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useRefData } from "../context/RefDataContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { StatusBadge, DeadlineBadge } from "../components/StatusBadge";
import VehicleColor from "../components/VehicleColor";
import PageHeader from "../components/PageHeader";
import ListSkeleton from "../components/ListSkeleton";
import EmptyState from "../components/EmptyState";
import { romeDate } from "../lib/businessValidation";
import { PhotoUploader } from "../components/PhotoUploader";
import { ProtectedFile } from "../components/ProtectedFile";
import { toast } from "sonner";
import { saveBlob } from "../lib/download";
import { VEHICLE_STATUS, GENERIC_STATUS, fmtNum, fmtEUR, fmtDate, labelize } from "../lib/format";
import {
  Gauge, MapPin, Fuel, Calendar, Euro, KeyRound, Loader2, Clock, Car,
  Wrench, ClipboardList, ShieldAlert, Receipt, FileText, Download, PenTool,
} from "lucide-react";

const TIMELINE_ICON = {
  assignments: ClipboardList, maintenances: Wrench, costs: Euro, refuelings: Fuel,
  damages: ShieldAlert, fines: Receipt, deadlines: FileText,
};

function MiniTable({ cols, rows, render }) {
  if (!rows.length) return <p className="py-8 text-center text-sm text-muted-foreground">Nessun dato disponibile</p>;
  return (
    <Table>
      <TableHeader><TableRow className="border-border hover:bg-transparent">{cols.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
      <TableBody>{rows.map((r, i) => <TableRow key={r.id || i} className="border-border">{render(r)}</TableRow>)}</TableBody>
    </Table>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const refData = useRefData();
  const { user } = useAuth();
  const canEditPhotos = can(user,"vehicles.operational");
  const [v, setV] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [data, setData] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);
  const [assignPdfBusy, setAssignPdfBusy] = useState(false);
  const [assignmentDetail, setAssignmentDetail] = useState(null);
  const [tab, setTab] = useState("panoramica");
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    // Another vehicle on the same page: nothing of the previous one stays on screen.
    setV(null); setTimeline([]); setData({}); setAssignmentDetail(null);
    setLoadError("");
    // A failed load says why and stays on this address, so the link can be retried or shared.
    api.get(`/vehicles/${id}`, {signal:controller.signal}).then((r) => {if(active)setV(r.data);}).catch((error) => {
      if(!active || error.code === 'ERR_SESSION_CHANGED') return;
      const status = error.response?.status;
      setLoadError(status === 403 || status === 404
        ? "Questo veicolo non esiste o non è visibile con il tuo accesso e la sede attiva."
        : apiError(error));
    });
    api.get(`/vehicles/${id}/timeline`, {signal:controller.signal}).then((r) => {if(active)setTimeline(r.data);}).catch(() => {});
    ["assignments", "maintenances", "refuelings", "costs", "deadlines", "damages", "fines"].filter(res=>can(user,`${res === "assignments" ? "rentals" : res}.read`)).forEach((res) => {
      api.get(`/${res}?vehicle_id=${id}`, {signal:controller.signal}).then((r) => {if(active)setData((d) => ({ ...d, [res]: r.data }));}).catch(() => {});
    });
    return () => {active = false;controller.abort();};
  // Auth scope remounts this page when permissions change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, retry]);

  if (!v && loadError) return (
    <Card className="mx-auto max-w-xl" role="alert">
      <EmptyState icon={Car} title="Impossibile aprire il veicolo" description={loadError}
        action={<div className="flex flex-wrap justify-center gap-2"><Button variant="outline" onClick={() => setRetry((n) => n + 1)}>Riprova</Button><Button onClick={() => navigate("/veicoli")}>Torna ai veicoli</Button></div>} />
    </Card>
  );
  if (!v) return <ListSkeleton rows={4} label="Caricamento veicolo…" />;

  const economic = can(user, "economic.summary");
  const locName = refData.locations.find((l) => l.id === v.location_id)?.name || "—";
  const supName = refData.suppliers.find((s) => s.id === v.supplier_id)?.name || "—";
  const specs = [
    { icon: Gauge, label: "Chilometraggio", value: v.mileage_current == null ? "—" : fmtNum(v.mileage_current) + " km" },
    { icon: Fuel, label: "Alimentazione", value: labelize(v.fuel) },
    { icon: MapPin, label: "Sede", value: locName },
    { icon: Calendar, label: "Immatricolazione", value: v.registration_year },
    ...(economic ? [{ icon: Euro, label: "Valore attuale", value: fmtEUR(v.current_value) }] : []),
    { icon: KeyRound, label: "Chiavi", value: v.keys_count },
  ];

  const D = (k) => data[k] || [];
  // Archive, restore and decisions return the saved vehicle; deletion leaves the page.
  const lifecycleChanged = (saved) => { setV((current) => ({ ...current, ...saved })); refData.reload(); };
  const lifecycleDeleted = () => { refData.reload(); navigate("/veicoli"); };

  // The three facts an operator looks for first, from the lists already loaded.
  const today = romeDate();
  const rental = D("assignments").find((a) => a.status === "in_corso" || a.status === "in_consegna")
    || D("assignments").filter((a) => a.status === "pianificata" && !a.is_archived).sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))[0];
  const nextDeadline = D("deadlines").filter((d) => d.expiry_date).sort((a, b) => String(a.expiry_date).localeCompare(String(b.expiry_date)))[0];
  const nextMaintenance = D("maintenances").filter((m) => !["completato", "annullato"].includes(m.status))
    .sort((a, b) => String(a.planned_date || a.opened_date || "9999").localeCompare(String(b.planned_date || b.opened_date || "9999")))[0];
  const situation = [
    {
      key: "availability", icon: KeyRound, label: "Disponibilità", tab: can(user, "rentals.read") ? "assegnazioni" : null,
      tone: { disponibile: "tone-green", in_manutenzione: "tone-amber", non_operativo: "tone-red" }[v.status] || "tone-sky",
      value: VEHICLE_STATUS[v.status]?.label || labelize(v.status),
      detail: v.availability_reason || (rental ? `${rental.status === "pianificata" ? "Prenotato" : "A noleggio"}: ${rental.assignee_name}, ${fmtDate(rental.start_date)} → ${fmtDate(rental.end_date_planned)}` : "Nessun noleggio in corso o prenotato"),
    },
    ...(can(user, "deadlines.read") ? [{
      key: "deadline", icon: Calendar, label: "Prossima scadenza", tab: "documenti",
      tone: nextDeadline && nextDeadline.expiry_date < today ? "tone-red" : "tone-sky",
      value: nextDeadline ? `${labelize(nextDeadline.category)} · ${fmtDate(nextDeadline.expiry_date)}` : "Nessuna registrata",
      detail: nextDeadline ? <DeadlineBadge expiry={nextDeadline.expiry_date} /> : null,
    }] : []),
    ...(can(user, "maintenances.read") ? [{
      key: "maintenance", icon: Wrench, label: "Manutenzione", tab: "manutenzioni", tone: nextMaintenance ? "tone-amber" : "tone-green",
      value: nextMaintenance ? labelize(nextMaintenance.type) : "Nessuna aperta",
      detail: nextMaintenance ? `${GENERIC_STATUS[nextMaintenance.status]?.label || labelize(nextMaintenance.status)}${nextMaintenance.planned_date ? ` · prevista il ${fmtDate(nextMaintenance.planned_date)}` : ""}` : null,
    }] : []),
  ];

  const savePhotos = async (photos) => {
    const { data: saved } = await api.put(`/vehicles/${id}`, { photos },{headers:{'If-Match':v.updated_at||''}});
    if (JSON.stringify((saved.photos || []).map((p) => p.path)) !== JSON.stringify(photos.map((p) => p.path))) {
      throw new Error("Le fotografie non sono state salvate");
    }
    setV((x) => ({ ...x, photos: saved.photos || [],updated_at:saved.updated_at }));
    toast.success("Fotografie aggiornate");
  };


  const downloadVehiclePDF = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/vehicles/${id}/pdf`, { responseType: "blob" });
      saveBlob(res.data, `scheda_${v.plate}.pdf`);
    } catch (e) { toast.error("Generazione PDF fallita"); } finally { setPdfBusy(false); }
  };

  const downloadAssignmentPDF = async (aid) => {
    setAssignPdfBusy(true);
    try {
      const res = await api.get(`/assignments/${aid}/pdf`, { responseType: "blob" });
      saveBlob(res.data, `assegnazione_${v.plate}.pdf`);
    } catch (e) { toast.error("Generazione PDF fallita"); } finally { setAssignPdfBusy(false); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <PageHeader
        icon={Car}
        trail={[{ label: "Veicoli", to: "/veicoli" }, { label: v.plate }]}
        eyebrow={<span className="inline-flex flex-wrap items-center gap-2"><span className="font-mono font-semibold text-primary">{v.plate}</span><VehicleColor color={v.color} size="lg" /></span>}
        title={`${v.brand} ${v.model}`}
        description={[v.version, v.vin && `Telaio ${v.vin}`, v.internal_id && `ID interno ${v.internal_id}`].filter(Boolean).join(" · ")}
        actions={<><StatusBadge status={v.status} kind="vehicle" />{!v.is_archived&&<VehicleTransfer vehicle={v} onTransferred={()=>{refData.reload();navigate("/veicoli");}}/>}<VehicleLifecycle user={user} vehicle={v} onChanged={lifecycleChanged} onDeleted={lifecycleDeleted}/>{can(user,"export")&&<Button onClick={downloadVehiclePDF} disabled={pdfBusy} data-testid="vehicle-pdf-btn">
            {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Scheda PDF
          </Button>}</>}
      />

      <VehicleLifecycleNotice user={user} vehicle={v} onChanged={lifecycleChanged} onDeleted={lifecycleDeleted} />

      {/* Situation now: what limits the vehicle and what comes next, each opening its tab. */}
      <div className="grid gap-3 md:grid-cols-3">
        {situation.map((item) => (
          <button key={item.key} type="button" onClick={() => item.tab && setTab(item.tab)} disabled={!item.tab}
            className={`flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors ${item.tab ? "hover:border-primary/40" : "cursor-default"}`}>
            <span className={`icon-tile h-9 w-9 ${item.tone || ""}`} aria-hidden="true"><item.icon className="h-5 w-5" /></span>
            <span className="min-w-0">
              <span className="block text-xs text-muted-foreground">{item.label}</span>
              <span className="block font-semibold">{item.value}</span>
              {item.detail && <span className="block text-sm text-muted-foreground">{item.detail}</span>}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-6">
        {specs.map((s) => (
          <div key={s.label} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
            <span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><s.icon className="h-4 w-4" /></span>
            <div className="min-w-0"><p className="text-xs text-muted-foreground">{s.label}</p><p className="break-words text-sm font-semibold">{s.value ?? "—"}</p></div>
          </div>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">Sede operativa: {locName} · Sede originaria: {refData.locations.find(site=>site.id===v.home_location_id)?.name || "—"} · {labelize(v.ownership)} · Fornitore {supName}. Le pratiche storiche conservano la propria sede.</p>

      <Tabs value={tab} onValueChange={setTab}>
        {/* The tabs wrap on a phone: the list grows with them, and each tab is easy to tap. */}
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 [&>button]:min-h-10 sm:[&>button]:min-h-8">
          <TabsTrigger value="panoramica" data-testid="tab-panoramica">Panoramica</TabsTrigger>
          {can(user,"rentals.read")&&<TabsTrigger value="assegnazioni">Assegnazioni</TabsTrigger>}
          {can(user,"maintenances.read")&&<TabsTrigger value="manutenzioni">Manutenzioni</TabsTrigger>}
          {can(user,"refuelings.read")&&<TabsTrigger value="rifornimenti">Rifornimenti</TabsTrigger>}
          {can(user,"costs.read")&&<TabsTrigger value="costi">Costi</TabsTrigger>}
          {can(user,"deadlines.read")&&<TabsTrigger value="documenti">Documenti</TabsTrigger>}
          {can(user,"damages.read")&&<TabsTrigger value="danni">Danni</TabsTrigger>}
          {can(user,"fines.read")&&<TabsTrigger value="multe">Multe</TabsTrigger>}
          <TabsTrigger value="fotografie" data-testid="tab-fotografie">Fotografie</TabsTrigger>
          <TabsTrigger value="cronologia" data-testid="tab-cronologia">Cronologia</TabsTrigger>
        </TabsList>

        <TabsContent value="panoramica">
          <Card className="border-border bg-card p-6">
            <h3 className="mb-4 font-heading text-lg font-semibold">Scheda tecnica completa</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
              {[["Targa", v.plate], ["Marca", v.brand], ["Modello", v.model], ["Versione", v.version], ["Categoria", labelize(v.category)], ["Tipologia", labelize(v.vehicle_type)], ["Telaio", v.vin], ["Alimentazione", labelize(v.fuel)], ["Cilindrata", v.displacement != null && v.displacement !== "" ? v.displacement + " cc" : "—"], ["Potenza", v.power_hp != null && v.power_hp !== "" ? v.power_hp + " CV" : "—"], ["Cambio", labelize(v.gearbox)], ["Colore", v.color ? <VehicleColor color={v.color} showName /> : "—"], ["Anno", v.registration_year], ["Data acquisto", fmtDate(v.purchase_date)], ...(economic ? [["Prezzo acquisto", fmtEUR(v.purchase_price)], ["Valore attuale", fmtEUR(v.current_value)]] : []), ["Scadenza contratto", fmtDate(v.contract_end)], ["Note", v.notes || "—"]].map(([k, val]) => (
                <div key={k} className="flex justify-between border-b border-border/60 pb-2"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{val}</span></div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="assegnazioni"><Card className="border-border bg-card p-4">
          {D("assignments").length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Nessun dato disponibile</p> : (
          <Table><TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Assegnatario</TableHead><TableHead>Inizio</TableHead><TableHead>Fine prevista</TableHead><TableHead>Firme</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
          <TableBody>{D("assignments").map((r) => (
            <TableRow key={r.id} className="cursor-pointer border-border" onClick={() => setAssignmentDetail(r)} data-testid={`assignment-row-${r.id}`}>
              <TableCell>{r.assignee_name}</TableCell><TableCell>{fmtDate(r.start_date)}</TableCell><TableCell>{fmtDate(r.end_date_planned)}</TableCell>
              <TableCell>{(r.signature_start || r.signature) ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><PenTool className="h-3.5 w-3.5" />Firmata</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
              <TableCell><StatusBadge status={r.status} /></TableCell>
            </TableRow>))}</TableBody></Table>)}
        </Card></TabsContent>
        <TabsContent value="manutenzioni"><Card className="border-border bg-card p-4"><MiniTable cols={["Tipo", "Data prevista", "Costo", "Stato"]} rows={D("maintenances")} render={(r) => (<>{[<TableCell key="1">{labelize(r.type)}</TableCell>, <TableCell key="2">{fmtDate(r.planned_date)}</TableCell>, <TableCell key="3">{fmtEUR(r.actual_cost || r.estimated_cost)}</TableCell>, <TableCell key="4"><StatusBadge status={r.status} /></TableCell>]}</>)} /></Card></TabsContent>
        <TabsContent value="rifornimenti"><Card className="border-border bg-card p-4"><MiniTable cols={["Data", "Litri/kWh", "Totale", "Stazione"]} rows={D("refuelings")} render={(r) => (<>{[<TableCell key="1">{fmtDate(r.date)}</TableCell>, <TableCell key="2">{r.liters}</TableCell>, <TableCell key="3">{fmtEUR(r.total)}</TableCell>, <TableCell key="4">{r.station}</TableCell>]}</>)} /></Card></TabsContent>
        <TabsContent value="costi"><Card className="border-border bg-card p-4"><MiniTable cols={["Data", "Categoria", "Totale", "Pagamento"]} rows={D("costs")} render={(r) => (<>{[<TableCell key="1">{fmtDate(r.date)}</TableCell>, <TableCell key="2">{labelize(r.category)}</TableCell>, <TableCell key="3">{fmtEUR(r.total)}</TableCell>, <TableCell key="4">{labelize(r.payment_method)}</TableCell>]}</>)} /></Card></TabsContent>
        <TabsContent value="documenti"><Card className="border-border bg-card p-4"><MiniTable cols={["Categoria", "Numero", "Scadenza", "Stato"]} rows={D("deadlines")} render={(r) => (<>{[<TableCell key="1">{labelize(r.category)}</TableCell>, <TableCell key="2">{r.number}</TableCell>, <TableCell key="3">{fmtDate(r.expiry_date)}</TableCell>, <TableCell key="4"><DeadlineBadge expiry={r.expiry_date} /></TableCell>]}</>)} /></Card></TabsContent>
        <TabsContent value="danni"><Card className="border-border bg-card p-4"><MiniTable cols={["Data", "Tipo", "Gravità", "Costo", "Stato"]} rows={D("damages")} render={(r) => (<>{[<TableCell key="1">{fmtDate(r.date)}</TableCell>, <TableCell key="2">{labelize(r.type)}</TableCell>, <TableCell key="3">{labelize(r.severity)}</TableCell>, <TableCell key="4">{fmtEUR(r.actual_cost || r.estimated_cost)}</TableCell>, <TableCell key="5"><StatusBadge status={r.status} /></TableCell>]}</>)} /></Card></TabsContent>
        <TabsContent value="multe"><Card className="border-border bg-card p-4"><MiniTable cols={["Data", "Tipo", "Importo", "Stato"]} rows={D("fines")} render={(r) => (<>{[<TableCell key="1">{fmtDate(r.date)}</TableCell>, <TableCell key="2">{labelize(r.type)}</TableCell>, <TableCell key="3">{fmtEUR(r.amount)}</TableCell>, <TableCell key="4"><StatusBadge status={r.status} /></TableCell>]}</>)} /></Card></TabsContent>

        <TabsContent value="fotografie">
          <Card className="border-border bg-card p-6">
            {!canEditPhotos && <p className="mb-3 text-sm text-muted-foreground">Fotografie in sola consultazione per il tuo profilo.</p>}
            <PhotoUploader value={v.photos || []} onChange={savePhotos} label="Fotografie del veicolo" readOnly={!canEditPhotos} />
          </Card>
        </TabsContent>

        <TabsContent value="cronologia">
          <Card className="border-border bg-card p-6">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Clock className="h-5 w-5 text-primary" /> Timeline eventi</h3>
            <div className="relative space-y-4 border-l border-border pl-6">
              {timeline.map((e, i) => {
                const Icon = TIMELINE_ICON[e.type] || Clock;
                return (
                  <div key={i} className="relative" data-testid={`timeline-item-${i}`}>
                    <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card"><Icon className="h-3 w-3 text-primary" /></span>
                    <div className="flex items-center justify-between">
                      <div className={e.type === "assignments" ? "cursor-pointer" : ""} onClick={() => e.type === "assignments" && setAssignmentDetail(e)}>
                        <p className="text-sm font-medium">{e.label}: {labelize(e.title)}{e.type === "assignments" && (e.signature || e.signature_start) ? <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-400"><PenTool className="h-3 w-3" />Firmata</span> : null}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(e.date)}</p>
                      </div>
                      {e.amount ? <span className="text-sm font-semibold text-sky-300">{fmtEUR(e.amount)}</span> : null}
                    </div>
                  </div>
                );
              })}
              {timeline.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessun evento registrato</p>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!assignmentDetail} onOpenChange={(o) => !o && setAssignmentDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader className="flex-row items-center justify-between gap-3 pr-8">
            <DialogTitle>Dettaglio assegnazione</DialogTitle>
            {assignmentDetail && can(user,"export") && (
              <Button size="sm" onClick={() => downloadAssignmentPDF(assignmentDetail.id)} disabled={assignPdfBusy} data-testid="assignment-pdf-btn">
                {assignPdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Scarica PDF
              </Button>
            )}
          </DialogHeader>
          <DialogDescription>Consulta le firme e le fotografie generali. Il PDF comprende anche i rilievi danni registrati.</DialogDescription>
          {assignmentDetail && (
          <div className="space-y-5 py-1"><RentalProgress assignment={assignmentDetail} compact/>
            <p className="text-sm text-muted-foreground">{assignmentDetail.assignee_name || assignmentDetail.assignee || "—"} · {fmtDate(assignmentDetail.start_date || assignmentDetail.date)}</p>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="mb-1 text-xs font-medium text-muted-foreground">Firma consegna</p>{assignmentDetail.signature_start ? <img src={assignmentDetail.signature_start} alt="firma consegna" className="w-full rounded-lg border border-border bg-white" /> : <p className="text-xs text-muted-foreground">Non presente</p>}</div>
              <div><p className="mb-1 text-xs font-medium text-muted-foreground">Firma riconsegna</p>{assignmentDetail.signature ? <img src={assignmentDetail.signature} alt="firma riconsegna" className="w-full rounded-lg border border-border bg-white" /> : <p className="text-xs text-muted-foreground">Non presente</p>}</div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Confronto foto prima / dopo</p>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="mb-1 text-xs text-muted-foreground">Prima ({(assignmentDetail.photos_start || []).length})</p><div className="grid grid-cols-2 gap-2">{(assignmentDetail.photos_start || []).map((p, idx) => (<ProtectedFile key={idx} file={p} alt="prima" imageClassName="h-20 w-full rounded border border-border object-cover" />))}{(assignmentDetail.photos_start || []).length === 0 && <p className="text-xs text-muted-foreground">Nessuna</p>}</div></div>
                <div><p className="mb-1 text-xs text-muted-foreground">Dopo ({(assignmentDetail.photos_end || []).length})</p><div className="grid grid-cols-2 gap-2">{(assignmentDetail.photos_end || []).map((p, idx) => (<ProtectedFile key={idx} file={p} alt="dopo" imageClassName="h-20 w-full rounded border border-border object-cover" />))}{(assignmentDetail.photos_end || []).length === 0 && <p className="text-xs text-muted-foreground">Nessuna</p>}</div></div>
              </div>
            </div>
          </div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
